import React, {useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import base_url from '../../baseUrls';
//import * as allConstants from '../../constants/allConstants';
import {TAG_FIELDS} from '../../constants/formFields/tagFields';
import MyPaginatedGrid from '../../pieces/grids/paginatedGridWrapper';
import ColumnDefinition from '../../pieces/grids/columnGenerator';
import StatusRenderer from '../../pieces/grids/statusRenderer';
import {NewPermissionGate} from '../../permissions/permissionGate';
import {TAG_ELEMENTS, tagsPageRules} from '../../permissions/tagAccessControl';
import Utils from '../../utils/utils';

/**
 * One object for the life of the page, not a fresh literal per render. The paginated grid resets its
 * datasource whenever `fetchParams` changes identity, so an inline literal refetched on every parent
 * render — including the add/edit modal opening and closing, which can land mid-draw and throw AG
 * Grid's "cannot get grid to draw rows when it is in the middle of drawing rows".
 */
const FETCH_PARAMS = {url: `${base_url.api}tagJobs/`, respKey: 'tags'};

const AllTagJobsGrid = (props) => {

  //console.log('inside alltagjobsgrid');
  //console.log(props);

  const viewController = props.viewController;
  const [components] = useState({
    'statusRenderer': StatusRenderer,
  });

  /**
   * Companies whose tags this user can see, resolved server-side and passed down by the
   * view (which holds the page back until it is loaded). Deriving it from redux
   * childCompanies missed sub-accounts whose parent link is recorded only on the child.
   */
  const tagCompanies = props.tagCompanies;
  const isParentCompany = tagCompanies.length > 1;

  const companyIdByName = useMemo(() => {
    const map = new Map();
    tagCompanies.forEach((c) => map.set(c.companyName, c.companyId));
    return map;
  }, [tagCompanies]);

  const tagColumns = {
    id: new ColumnDefinition({field: TAG_FIELDS.ID, hide: true}),
    // Filtered by companyId because companyName is resolved per row server-side and is
    // not stored on the tag, so it is not a filterable field.
    companyName: new ColumnDefinition({
      field: TAG_FIELDS.COMPANY_ID,
      headerName: TAG_FIELDS.COMPANY_NAME.label,
      sortable: true,
      filter: 'agSetColumnFilter',
      filterValues: tagCompanies.map((c) => c.companyName),
      convertSetFilterValuesToApi: (values) =>
        values.map((name) => companyIdByName.get(name) ?? name),
      valueGetter: (({data}) => data?.[TAG_FIELDS.COMPANY_NAME.api_name] ?? ''),
    }),
    tagName: new ColumnDefinition({field: TAG_FIELDS.TAG_NAME,
      lockPinned: true,
      pinned: 'left',
      checkboxSelection: true,
      width: 350,
      wrapText: true
    }),
    // createdBy is a name assembled from a user lookup server-side, so there is nothing
    // to filter on; offering a filter here just errors the request.
    createdBy: new ColumnDefinition({
      field: TAG_FIELDS.CREATED_BY,
      removeDefaultFilter: true,
    }),
    createdAt: new ColumnDefinition({
      field: TAG_FIELDS.CREATED_AT,
      sortable: true,
      filter: 'agDateColumnFilter',
      valueGetter: (({data}) => {
        
        if (data) {
          return Utils.formatIsoDateString(data?.[TAG_FIELDS.CREATED_AT.api_name], {date: true});
        }
        return null;
      })
    })

  }

  const getColumns = () => {
    const columns = [tagColumns.id];
    // Only a prime company sees more than one company's tags; for everyone else the
    // column is a single repeated value.
    if (isParentCompany) {
      columns.push(tagColumns.companyName);
    }
    return [
      ...columns,
      tagColumns.tagName,
      tagColumns.createdBy,
      tagColumns.createdAt,
    ];
  }

  let columnDefs = useMemo(()=> {
    return getColumns();
  }, [isParentCompany, tagCompanies]);


  const editTagHandler = (tagData)=> {
    props.editTagHandler(tagData);
  };

  const deleteTagHandler = (tagData)=> {
    props.deleteTagHandler(tagData);
  };

  const getContextMenuItems = (params)=> {
    // console.log('params', params?.node?.data, user?.id);
    if(!params.node) return [];

    const standardActions = [
      {
        name:  'Export Selected (.xlsx)',
        action: () => params.api.exportDataAsExcel(
          {onlySelected: true}
        )
      },
      'copy',
    ];

    const actions = {
      edit: {
        name: 'Edit',
        disabled: viewController.selectedMultipleRows(),
        action: () => {
          editTagHandler(params?.node?.data);
        },
      },
      delete: {
        name: 'Delete',
        cssClasses: ['redFont', 'bold'],
        disabled: viewController.selectedMultipleRows(),
        action: () => {
          deleteTagHandler(params?.node?.data);
        },
      },
    };

    let allowedActions = [];
    // if(NewPermissionGate({
    //   user: viewController.user,
    //   elementPermissions: tagsPageRules[TAG_ELEMENTS.editTag],
    // })) {
      allowedActions.push(actions.edit);
      allowedActions.push(actions.delete);
    //}

    allowedActions = [...allowedActions, ...standardActions];

    return allowedActions;
  };

  const ls_name = 'tagJobs_grid';

  return (
    <MyPaginatedGrid columnDefs={columnDefs}
      components={components}
      contextMenu={getContextMenuItems}
      ls_name={ls_name}
      registerGrid={viewController.setGridToParent}
      registerTempGrid={viewController.setTempGridToParent} 
      fetchParams={FETCH_PARAMS}
      onFetchFail={viewController.onFetchFail}
      pagination={props.pagination}
    />
  );
};

AllTagJobsGrid.propTypes = {
  // editTagHandler: PropTypes.func.isRequired,
  // deleteTagtHandler: PropTypes.func.isRequired,
  pagination: PropTypes.instanceOf(Object).isRequired,
  viewController: PropTypes.instanceOf(Object).isRequired,
  tagCompanies: PropTypes.arrayOf(PropTypes.shape({
    companyId: PropTypes.string,
    companyName: PropTypes.string,
    isOwnCompany: PropTypes.bool,
  })),
};

AllTagJobsGrid.defaultProps = {
  tagCompanies: [],
};
export default AllTagJobsGrid;
